import { useEffect, useRef, useState } from 'react';
import { useMatch } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  useLiveSession,
  usePauseSession,
  useResumeSession,
  useStartSession,
  useStopSession,
} from '@/lib/adminApi';
import type { TimeSessionSummary } from '@ventin/shared';

// ---------------------------------------------------------------------------
// Notification math (TIME-02)
// ---------------------------------------------------------------------------

/** Next 20-minute increment tip boundary in minutes. */
function nextTipMinutes(durationMinutes: number): number {
  return Math.ceil(Math.max(durationMinutes, 0.0001) / 20) * 20;
}

/** Format milliseconds as HH:MM:SS */
function formatMs(ms: number): string {
  const totalSecs = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSecs / 3600);
  const m = Math.floor((totalSecs % 3600) / 60);
  const s = totalSecs % 60;
  return [h, m, s].map((v) => String(v).padStart(2, '0')).join(':');
}

// ---------------------------------------------------------------------------
// TimerWidget
// ---------------------------------------------------------------------------

/**
 * TimerWidget — persistent real-time timer UI.
 *
 * Mounted in AdminLayout so the timer state survives page navigation.
 * Reads live session from useLiveSession(); ticks locally via setInterval.
 *
 * TIME-01: start/pause/resume/stop controls
 * TIME-02: 5-minute browser Notification before each 20-min increment tip
 */
export function TimerWidget() {
  const { data, isLoading } = useLiveSession();
  const session: TimeSessionSummary | null = data?.session ?? null;

  const startMut = useStartSession();
  const pauseMut = usePauseSession();
  const resumeMut = useResumeSession();
  const stopMut = useStopSession();

  // Local elapsed state — updated by setInterval when RUNNING
  const [elapsedMs, setElapsedMs] = useState(0);

  // Track highest tip minute already fired to dedupe notifications
  const firedTipRef = useRef<number>(0);

  // Request Notification permission on first mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      void Notification.requestPermission();
    }
  }, []);

  // Re-sync firedTipRef when session changes (new session = reset tip tracking)
  const sessionId = session?.id;
  useEffect(() => {
    firedTipRef.current = 0;
  }, [sessionId]);

  // Local clock tick — only when RUNNING
  useEffect(() => {
    if (!session || session.state !== 'RUNNING') return;

    const tick = () => {
      // Dev-only time shim: if __timerBoostStart is set in localStorage, use it
      // as the effective "now - startedAt" anchor so the Notification can be
      // triggered manually without waiting the real 15 minutes.
      // Usage: localStorage.setItem('__timerBoostStart', String(Date.now() - 14 * 60 * 1000))
      // Dev shim: __timerBoostStart overrides elapsed so the agent can fast-forward
      // to near the 15:00 mark without waiting 15 real minutes.
      const boostRaw = import.meta.env.DEV ? localStorage.getItem('__timerBoostStart') : null;
      const sinceStart = boostRaw
        ? Date.now() - Number(boostRaw)
        : Date.now() - new Date(session.startedAt).getTime();
      const totalMs = session.durationMinutes * 60_000 + sinceStart;
      setElapsedMs(totalMs);

      // Notification logic (TIME-02)
      const elapsedMinutes = totalMs / 60_000;
      const nextTip = nextTipMinutes(elapsedMinutes);
      const fireTrigger = nextTip - 5;

      if (
        elapsedMinutes >= fireTrigger &&
        nextTip > firedTipRef.current &&
        'Notification' in window &&
        Notification.permission === 'granted'
      ) {
        firedTipRef.current = nextTip;
        const ticketTitle = session.ticketIds[0] ?? 'ticket';
        new Notification('5 minutes until next 20-min increment', {
          body: `Ticket: ${ticketTitle}`,
          icon: '/favicon.ico',
          tag: `session-${session.id}-tip-${String(nextTip)}`,
        });
      }
    };

    tick(); // immediate first tick
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [session]);

  // When PAUSED, compute elapsed from durationMinutes only (no live ticking)
  useEffect(() => {
    if (!session || session.state !== 'RUNNING') {
      setElapsedMs((session?.durationMinutes ?? 0) * 60_000);
    }
  }, [session]);

  // Detect current ticket from route
  const ticketMatch = useMatch('/admin/tickets/:id');
  const currentTicketId = ticketMatch?.params.id ?? null;

  // ---------------------------------------------------------------------------
  // Derived state
  // ---------------------------------------------------------------------------

  const isRunning = session?.state === 'RUNNING';
  const isPaused = session?.state === 'PAUSED';
  const hasLiveSession = isRunning || isPaused;
  const displayMs = isRunning ? elapsedMs : (session?.durationMinutes ?? 0) * 60_000;

  const canStart = !hasLiveSession && currentTicketId !== null;
  const busy = startMut.isPending || pauseMut.isPending || resumeMut.isPending || stopMut.isPending;

  function handleStart() {
    if (!currentTicketId) return;
    startMut.mutate({ ticketIds: [currentTicketId] });
  }

  function handlePause() {
    if (!session) return;
    pauseMut.mutate({ id: session.id });
  }

  function handleResume() {
    if (!session) return;
    resumeMut.mutate({ id: session.id });
  }

  function handleStop() {
    if (!session) return;
    stopMut.mutate({ id: session.id });
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span>Loading timer…</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {/* Clock display */}
      <span className="font-mono text-sm tabular-nums w-[80px] text-center">
        {formatMs(displayMs)}
      </span>

      {/* Controls */}
      {!hasLiveSession && (
        <Button
          size="sm"
          variant="outline"
          onClick={handleStart}
          disabled={!canStart || busy}
          title={!canStart ? 'Open a ticket to start a timer' : 'Start timer'}
        >
          Start
        </Button>
      )}

      {isRunning && (
        <Button size="sm" variant="outline" onClick={handlePause} disabled={busy}>
          Pause
        </Button>
      )}

      {isPaused && (
        <Button size="sm" variant="outline" onClick={handleResume} disabled={busy}>
          Resume
        </Button>
      )}

      {hasLiveSession && (
        <Button size="sm" variant="destructive" onClick={handleStop} disabled={busy}>
          Stop
        </Button>
      )}

      {/* Notification fallback banner — shown when permission denied */}
      {'Notification' in window && Notification.permission === 'denied' && isRunning && (
        <span className="text-xs text-orange-600">Notifications blocked</span>
      )}
    </div>
  );
}
